import React, { createRef } from "react";
import "./sorterStyles.scss";

import { MicroscopeSlider } from "./MicroscopeSlider";
import { connect } from "react-redux";
import {
  setCurrentMagnification,
  changeFocus,
  adjustLight,
  toggleLight,
  changePosition,
  changeKnob,
  storeResultsImage,
  updateMicroscopeImage,
  changeViewportPosition,
} from "./Store/actions";
import { FormattedMessage } from "react-intl";
import { SimManager } from "@learningblocks/simulation-engine";
import Toast from "../toast/Toast";
import { toast } from "react-toastify";
import { css } from "glamor";
import html2canvas from "html2canvas";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { CloseButton } from "@learningblocks/storybook-components/lib/FullscreenModal";

interface Magnification {
  value: string;
  color: string;
  default?: boolean;
  width?: number;
  height?: number;
}

interface ImageCapturing {
  image: string;
  value: string;
}

interface SliderSettings {
  type: string;
  min: number;
  max: number;
  step: number;
  value: number;
  left?: string;
  right?: string;
}

interface position {
  x: number;
  y: number;
  type?: string;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  notebookCollapsed: boolean;
  magnifications: Array<Magnification>;
  selectedMagnification: Magnification;
  setCurrentMagnification: (data: Magnification) => void;
  changeFocus: (data: object) => void;
  focusStates: Array<SliderSettings>;
  lightIntensity: number;
  adjustLight: (value: number) => void;
  toggleLight: () => void;
  lightState?: string;
  navImagePosition: position;
  viewPortImagePosition: position;
  changePosition: (type: string) => void;
  changeViewportPosition: (type: string) => void;
  changeKnob: (data: string) => void;
  knob: string;
  stageHeight?: number;
  updateHeight?: (data: number) => void;
  storeResultsImage: (data: ImageCapturing) => void;
  viewPortImage: string;
  updateMicroscopeImage: (data: string) => void;
  baseMicroscope: string;
  centeredViewPortPosition: position;
}

interface sliderStates {
  blur: number;
  fineBlur: number;
  stage: number;
  fineStage: number;
  captured?: boolean;
  userScale: number;
  sceneScale: number;
  fineSliderPosition: number;
  coarseSliderPosition: number;
  idealFinePosition: number;
  idealCoarsePosition: number;
  navImagePosition: {
    x: number;
    y: number;
  };
}

interface ModalState {
  sorter: ModalState;
  selectedMagnification: Magnification;
  magnifications: Array<Magnification>;
  focusStates: Array<SliderSettings>;
  lightIntensity: number;
  toggleLight?: string;
  lightState?: string;
  navImagePosition: position;
  viewPortImagePosition: position;
  centeredViewPortPosition: position;
  knob: string;
  viewPortImage: string;
  blur: number;
  baseMicroscope: string;
}

const DIMENSIONS = {
  width: 1350,
  height: 600,
};

class Sorter extends React.Component<ModalProps, sliderStates> {
  preventAnimationModal: boolean;
  blurValue: number;
  focusBlurValue: number;
  lightIntensitySlider: SliderSettings;
  viewPortImage: string;
  stagePosition?: number;
  stageHeight: number;
  triggered: boolean;
  query: MediaQueryList;
  nosepiece: boolean;
  navImage: boolean;
  biggerScreensQuery: MediaQueryList;

  private ref: React.RefObject<HTMLDivElement>;

  constructor(props: ModalProps) {
    super(props);
    this.preventAnimationModal = false;
    this.blurValue = 0;
    this.focusBlurValue = 0;
    this.stagePosition = 0;
    this.stageHeight = 0;
    this.lightIntensitySlider = {
      type: "Light",
      left: "Dimmer",
      right: "Brighter",
      min: 0.25,
      max: 1,
      step: 0.05,
      value: 0.25,
    };
    this.viewPortImage = "";
    this.triggered = false;
    this.query = window.matchMedia("(min-height: 900px)");
    this.biggerScreensQuery = window.matchMedia("(max-width: 1680px)");
    this.ref = createRef();
    this.nosepiece = false;
    this.navImage = false;

    this.state = {
      blur: 50,
      stage: 0,
      fineBlur: 5,
      fineStage: 0,
      captured: false,
      sceneScale: 1,
      userScale: 1,
      fineSliderPosition: 5,
      coarseSliderPosition: 5,
      idealFinePosition: 1,
      idealCoarsePosition: 8,
      navImagePosition: {
        x: 0,
        y: 0,
      },
    };
  }

  componentDidUpdate(): void {
    if (!this.props.isOpen) {
      this.preventAnimationModal = true;
    }

    if (this.props.isOpen) {
      if (
        SimManager.getInstance().getState("Microscope") === "slide2Inserted"
      ) {
        if (
          this.props.navImagePosition.x <= 15 &&
          this.props.navImagePosition.x >= 11 &&
          this.props.navImagePosition.y >= -2 &&
          this.props.navImagePosition.y <= 2
        ) {
          SimManager.getInstance().updateState("Stage", "centered");
        }
      } else {
        if (
          this.props.navImagePosition.x <= -3 &&
          this.props.navImagePosition.x >= -5 &&
          this.props.navImagePosition.y >= 4 &&
          this.props.navImagePosition.y <= 6
        ) {
          SimManager.getInstance().updateState("Stage", "centered");
        }
      }
      if (this.getStageHeight() === 17.5) {
        SimManager.getInstance().updateState("Coarse", "lowered");
      }
      if (this.props.selectedMagnification.value === "2.5x") {
        SimManager.getInstance().updateState("Lens", "2.5x");
      } else if (this.props.selectedMagnification.value === "10x") {
        SimManager.getInstance().updateState("Lens", "10x");
      } else if (this.props.selectedMagnification.value === "40x") {
        SimManager.getInstance().updateState("Lens", "40x");
      }
      if (this.props.lightIntensity === 1) {
        SimManager.getInstance().updateState("LightIntensity", "adjusted");
      }
      if (SimManager.getInstance().getState("Stage") === "centered") {
        if (
          this.state.coarseSliderPosition === this.state.idealCoarsePosition
        ) {
          SimManager.getInstance().updateState("Coarse", "rough");
        }
        if (this.state.fineSliderPosition === this.state.idealFinePosition) {
          SimManager.getInstance().updateState("FineFocus", "focused");
        }
        if (
          this.state.coarseSliderPosition === this.state.idealCoarsePosition &&
          this.state.fineSliderPosition === this.state.idealFinePosition
        ) {
          SimManager.getInstance().updateState("IdealMicroscopeBlur", "passed");
        }
      }
    }
  }

  private onMediaQueryChange = () => {
    this.updateMicroscope(this.query);
  };

  componentDidMount(): void {
    this.updateMicroscope(this.query);

    this.setState({
      stage: 50,
    });

    this.props.setCurrentMagnification({
      value: "10x",
      color: "#FAAC48",
      default: true,
      width: 60,
      height: 60,
    });

    this.adjustLightIntensitySlider(0.5);

    if (typeof this.query.addEventListener === "function") {
      this.query.addEventListener("change", this.onMediaQueryChange);
    } else if (typeof this.query.addListener === "function") {
      this.query.addListener(this.onMediaQueryChange);
    }
    this.props.changeKnob("images/sorter/nobs.svg");

    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  componentWillUnmount(): void {
    // tear down the media‐query listener
    if (typeof this.query.removeEventListener === "function") {
      this.query.removeEventListener("change", this.onMediaQueryChange);
    } else if (typeof this.query.removeListener === "function") {
      this.query.removeListener(this.onMediaQueryChange);
    }

    window.removeEventListener("resize", this.handleResize);
  }

  /**
   *
   * @returns Current screen width and height dimensions
   */
  getDimensions = () => DIMENSIONS;

  handleResize = () => {
    const height = window.innerHeight;
    const width = window.innerWidth;
    const heightRatio = height / this.getDimensions().height;
    const widthRatio = width / this.getDimensions().width;
    const scale = Math.min(heightRatio, widthRatio);

    this.setState({
      sceneScale: scale,
    });

    SimManager.getInstance().setScales(this.state.userScale, scale);
  };

  /**
   *
   * @param type Type of toast message
   * @param msg The message to be displayed on the toast componentt
   * @param title Toast title
   */
  makeToast = (type: string, msg: string, title: string) => {
    let color = "";
    if (type === "HINT") {
      color = "yellow";
    } else if (type === "SUCCESS") {
      color = "#4CAF50";
    } else {
      color = "red";
    }

    toast(<Toast type={type} msg={msg} title={title} />, {
      className: css({
        border: "2px solid " + color,
        borderRadius: "6px",
        marginLeft: "-200px",
        minWidth: "400px",
      }),
      progressClassName: css({
        background: color,
      }),
      position: "top-center",
      autoClose: 5000,
    });
  };
  /**
   * Renders image on viewport based on various microscope states
   */
  viewportStates = (): string => {
    const microscopeState = SimManager.getInstance().getState("Microscope");
    const wipeState = SimManager.getInstance().getState("MicroscopeWipe");
    const slideState = SimManager.getInstance().getState("MysterySlideY");

    if (
      microscopeState === "plugged" ||
      wipeState === "boxed" ||
      wipeState === "ejected"
    ) {
      this.navImage = false;
      this.viewPortImage = "images/sorter/Dirt.svg";
    } else if (microscopeState === "wiped") {
      this.navImage = false;
      this.viewPortImage = "images/sorter/Plate.svg";
    } else if (
      slideState === "droppedOnMicroscope" &&
      microscopeState === "slideInserted"
    ) {
      this.navImage = true;
      this.viewPortImage = `images/sorter/leaf.webp`;
    } else if (
      slideState === "slide2DroppedOnMicroscope" ||
      microscopeState === "slide2Inserted"
    ) {
      this.navImage = true;
      this.viewPortImage = "images/sorter/tapeworm_v2.webp";
    }
    if (SimManager.getInstance().getState("FineFocus") === "focused") {
      // this.viewPortImage =
      //   this.props.selectedMagnification.value && `images/sorter/leaf.webp`;
    }
    return this.viewPortImage;
  };

  /**
   * Captures the current image on the viewport and displays on the results
   */
  captureImage = (): void => {
    if (this.ref.current) {
      html2canvas(this.ref.current, { backgroundColor: null }).then(
        (canvas: HTMLCanvasElement) => {
          // Convert the canvas to a data URL
          const imgData = canvas.toDataURL("image/png");

          this.setState({
            captured: true,
          });

          setTimeout(() => {
            this.setState({
              captured: false,
            });
          }, 1000);

          SimManager.getInstance().updateState("Camera", "captured");
          this.makeToast("SUCCESS", "Image captured and saved to Results", "");
          this.props.storeResultsImage({
            image: imgData,
            value: this.props.selectedMagnification.value,
          });
        }
      );
    }
  };

  /**
   * When any of the fine/coarse positions change
   */

  onChangeOfSliderPosition(): number {
    const differenceInFine: number =
      this.state.idealFinePosition - this.state.fineSliderPosition;
    const differenceInCoarse: number =
      this.state.idealCoarsePosition - this.state.coarseSliderPosition;

    const fineBlurPerStep = 0.25;
    const coarseBlurPerStep = 2.5;

    const fineBlurImpact: number = differenceInFine * fineBlurPerStep;
    const coarseBlurImpact: number = differenceInCoarse * coarseBlurPerStep;

    const totalBlur = fineBlurImpact + coarseBlurImpact;

    this.blurValue = Math.abs(totalBlur);

    return this.blurValue;
  }

  getStageHeight(): number {
    const finePixelPerStep = 0.5;
    const coarsePixelPerStep = 3;

    const stageDifferenceInFine: number = 5 - this.state.fineSliderPosition;
    const stageDifferenceInFCoarse: number =
      5 - this.state.coarseSliderPosition;

    const fineStageImpact: number = stageDifferenceInFine * finePixelPerStep;
    const coarseStageImpact: number =
      stageDifferenceInFCoarse * coarsePixelPerStep;

    const totalStageHeight = fineStageImpact + coarseStageImpact;

    this.stageHeight = totalStageHeight;
    return this.stageHeight;
  }

  /**
   *
   * @returns Path to the nosepiece
   */
  renderNosepieceMagnification(): string {
    let nosepieceMagnification: string;
    switch (this.props.selectedMagnification.value) {
      case "2.5x":
        nosepieceMagnification = "images/sorter/Property 1=4.svg";
        break;
      case "40x":
        nosepieceMagnification = "images/sorter/Property 1=40.svg";
        break;
      default:
        nosepieceMagnification = "images/sorter/Property 1=10.svg";
    }

    return nosepieceMagnification;
  }

  private openAnimationModal = (value: string) => {
    switch (value) {
      case "2.5x":
        SimManager.getInstance().openModal(
          "LenseAnimation1",
          "LenseAnimation1"
        );
        break;
      case "40x":
        SimManager.getInstance().openModal(
          "LenseAnimation3",
          "LenseAnimation3"
        );
        break;
      default:
        SimManager.getInstance().openModal(
          "LenseAnimation2",
          "LenseAnimation2"
        );
    }
    this.triggered = false;
  };

  /**
   *
   * @param magnification Current magnification that the microscope lenses are set to
   */
  updateMagnification = (magnification: Magnification): void => {
    if (
      this.props.selectedMagnification.value === "2.5x" &&
      magnification.value === "40x"
    ) {
      this.makeToast(
        "WARNING",
        "You need to gradually change the objective lense. Switch to 10x before 40x.",
        "Magnification"
      );
    } else {
      if (magnification.value === "2.5x") {
        this.setState({
          idealCoarsePosition: 7,
          idealFinePosition: 8,
        });
        SimManager.getInstance().updateState("IdealMicroscopeBlur", "rough");
      }

      if (magnification.value === "10x") {
        this.setState({
          idealCoarsePosition: 8,
          idealFinePosition: 1,
        });
        SimManager.getInstance().updateState("IdealMicroscopeBlur", "rough");
      }

      if (magnification.value === "40x") {
        this.setState({
          idealCoarsePosition: 6,
          idealFinePosition: 9,
        });
        SimManager.getInstance().updateState("IdealMicroscopeBlur", "rough");
      }
      this.props.setCurrentMagnification(magnification);
      this.openAnimationModal(magnification.value);

      if (SimManager.getInstance().getState("Camera") === "captured") {
        this.setState({ blur: 70, stage: 70, fineBlur: 0 });
        SimManager.getInstance().updateState("FineFocus", "rough");
        SimManager.getInstance().updateState("Camera", "disabled");
      }
    }
  };

  /**
   *
   * @param value focus value
   * Updates the range focus value on drag of slider
   */
  setFocusValue = (data: SliderSettings, sliderValue: number): void => {
    this.preventAnimationModal = true;
    this.props.changeFocus({ data: data, sliderValue: sliderValue });
  };

  updateMicroscope = (query: MediaQueryList): void => {
    if (query.matches) {
      this.nosepiece = true;
      this.props.updateMicroscopeImage(`images/sorter/microscope_hook.svg`);
    } else {
      this.nosepiece = false;
      this.props.updateMicroscopeImage(`images/sorter/plain_microscope.svg`);
    }
  };

  /**
   *
   * @param value Range slider value
   * Adjusts the light intensity based off the slider value
   */
  adjustLightIntensitySlider = (value: number): void => {
    this.preventAnimationModal = true;
    this.props.adjustLight(value);
  };

  isKeyboardEvent = (
    x: React.KeyboardEvent | React.MouseEvent
  ): x is React.KeyboardEvent => !!(x as unknown as React.KeyboardEvent).key;

  /**
   * Toggle the light state between on and off
   */
  toggleLight = (event: React.KeyboardEvent | React.MouseEvent): void => {
    if (
      event &&
      this.isKeyboardEvent(event) &&
      !(event.key === "Enter" || event.key === " ")
    )
      return;
    this.preventAnimationModal = true;
    if (SimManager.getInstance().getState("MicroscopeLampLight") === "off") {
      SimManager.getInstance().updateState("MicroscopeLampLight", "on");
      this.adjustLightIntensitySlider(0.5);
    } else {
      SimManager.getInstance().updateState("MicroscopeLampLight", "off");
    }
    this.props.toggleLight();
  };

  changeImagePosition = (type: string): void => {
    this.preventAnimationModal = true;
    this.props.changeViewportPosition(type);
    this.props.changePosition(type);
  };

  /**
   *
   * @param type Focus Slider type
   */
  updateKnobOnHover = (type: string): void => {
    if (type === "Coarse") {
      this.props.changeKnob("images/sorter/coarse_nob.svg");
    }
    if (type === "Fine") {
      this.props.changeKnob("images/sorter/fine_nob.svg");
    }
  };

  LenseMagnifications() {
    const lenseMagnifications = this.props.magnifications;
    return (
      <div className="lenseSettings">
        <div className="lenseSettings__heading">
          <h5>Lenses</h5>
        </div>
        <div className="lenseSettings__buttons">
          {lenseMagnifications.map((magnification) => (
            <div
              role="button"
              tabIndex={0}
              aria-label={`${magnification?.value} lens`}
              className={`lenseSettings__buttons--${magnification?.value.includes(".") ? magnification?.value.replace(".", "") : magnification.value}`}
              style={{
                backgroundColor:
                  magnification.value ===
                  this.props.selectedMagnification?.value
                    ? this.props.selectedMagnification.color
                    : "",
                color:
                  magnification.value ===
                  this.props.selectedMagnification?.value
                    ? "#FFFFFF"
                    : "",
              }}
              onClick={() => {
                this.updateMagnification(magnification);
                if (this.preventAnimationModal)
                  this.preventAnimationModal = false;
              }}
              onKeyDown={(e) => {
                if (!(e.key === "Enter" || e.key === " ")) return;
                this.updateMagnification(magnification);
                if (this.preventAnimationModal)
                  this.preventAnimationModal = false;
              }}
            >
              <p>
                <FormattedMessage
                  id={magnification.value}
                  defaultMessage={magnification.value}
                  description=""
                />
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  private holdInterval: ReturnType<typeof setTimeout> | null = null;

  private startChangingPosition = (
    direction: "up" | "down" | "left" | "right"
  ) => {
    this.changeImagePosition(direction);
    this.holdInterval = setInterval(() => {
      this.changeImagePosition(direction);
    }, 100); // Adjust for speed
  };

  private stopChangingPosition = () => {
    if (this.holdInterval) {
      clearInterval(this.holdInterval);
      this.holdInterval = null;
    }
  };

  Navigation() {
    const { x, y } = this.props.navImagePosition;
    const isUpDisabled = y >= 50;
    const isDownDisabled = y <= -50;
    const isLeftDisabled = x >= 50;
    const isRightDisabled = x <= -50;

    const getButtonStyle = (disabled: boolean): React.CSSProperties => ({
      opacity: disabled ? 0.3 : 1,
      pointerEvents: disabled ? "none" : "auto",
    });

    return (
      <div className="navigationSettings">
        <div className="navigationSettings__heading">
          <h5>Navigation</h5>
        </div>
        <div className="navigationSettings__configs">
          <div className="navigationSettings__configs__stage">
            {(SimManager.getInstance().getState("Microscope") ===
              "slideInserted" ||
              SimManager.getInstance().getState("Microscope") ===
                "slide2Inserted") && (
              <img
                className="navigationSettings__configs__stage__slideImage"
                src={this.viewportStates()}
                alt="Specimen"
                style={{
                  transform: `translate(${this.props.navImagePosition.x * -1}%, ${this.props.navImagePosition.y * -1}%)`,
                }}
              ></img>
            )}
            <div
              className="navigationSettings__configs__stage__eclipse"
              style={{
                width: this.props.selectedMagnification.value
                  ? `${150 / parseFloat(this.props.selectedMagnification.value)}%`
                  : "100%",
                height: this.props.selectedMagnification.value
                  ? `${150 / parseFloat(this.props.selectedMagnification.value)}%`
                  : "100%",
                backgroundColor:
                  SimManager.getInstance().getState("Microscope") ===
                    "slideInserted" ||
                  SimManager.getInstance().getState("Microscope") ===
                    "slide2Inserted"
                    ? "transparent"
                    : "rgba(0, 0, 0, 0.80)",
              }}
            ></div>
          </div>
          <div
            aria-label="Camera control buttons"
            className="navigationSettings__configs--cameraButtons"
          >
            <div></div>

            {/* Up */}
            <div
              role="button"
              tabIndex={0}
              aria-label="Nav Button Up"
              className="navigationSettings__configs--cameraButtons-1"
              style={getButtonStyle(isUpDisabled)}
              onMouseDown={() => this.startChangingPosition("up")}
              onMouseUp={this.stopChangingPosition}
              onMouseLeave={this.stopChangingPosition}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !isUpDisabled) {
                  this.changeImagePosition("up");
                }
              }}
            >
              <img src="images/sorter/arrow-up.svg" alt="Up arrow" />
            </div>

            <div></div>

            {/* Left */}
            <div
              role="button"
              tabIndex={0}
              aria-label="Nav Button Left"
              className="navigationSettings__configs--cameraButtons-2"
              style={getButtonStyle(isLeftDisabled)}
              onMouseDown={() => this.startChangingPosition("left")}
              onMouseUp={this.stopChangingPosition}
              onMouseLeave={this.stopChangingPosition}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !isLeftDisabled) {
                  this.changeImagePosition("left");
                }
              }}
            >
              <img src="images/sorter/arrow-up.svg" alt="Left arrow" />
            </div>

            {/* Camera */}
            <div
              role="button"
              tabIndex={0}
              aria-label="Camera button"
              className="navigationSettings__configs--cameraButtons-3"
              onClick={this.captureImage}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") this.captureImage();
              }}
            >
              <img src="images/sorter/camera.svg" alt="Take photograph" />
            </div>

            {/* Right */}
            <div
              role="button"
              tabIndex={0}
              aria-label="Nav Button Right"
              className="navigationSettings__configs--cameraButtons-4"
              style={getButtonStyle(isRightDisabled)}
              onMouseDown={() => this.startChangingPosition("right")}
              onMouseUp={this.stopChangingPosition}
              onMouseLeave={this.stopChangingPosition}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !isRightDisabled) {
                  this.changeImagePosition("right");
                }
              }}
            >
              <img src="images/sorter/arrow-up.svg" alt="Right arrow" />
            </div>

            <div></div>

            {/* Down */}
            <div
              role="button"
              tabIndex={0}
              aria-label="Nav Button Down"
              className="navigationSettings__configs--cameraButtons-5"
              style={getButtonStyle(isDownDisabled)}
              onMouseDown={() => this.startChangingPosition("down")}
              onMouseUp={this.stopChangingPosition}
              onMouseLeave={this.stopChangingPosition}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !isDownDisabled) {
                  this.changeImagePosition("down");
                }
              }}
            >
              <img src="images/sorter/arrow-up.svg" alt="Down arrow" />
            </div>

            <div></div>
          </div>
        </div>
      </div>
    );
  }

  Settings() {
    return (
      <div className="sorterSettings">
        <div className="sorterSettings__heading">
          <h5>Settings</h5>
        </div>
        <div className="sorterSettings__configs">
          <div className="sorterSettings__configs--focus">
            {this.props.focusStates.map((state: SliderSettings) => (
              <div
                className={`sorterSettings__configs--focus-${state.type}`}
                aria-label="Range Sliders adjusting microscope stage height and blur value"
                onMouseOver={() => this.updateKnobOnHover(state.type)}
                onFocus={() => this.updateKnobOnHover(state.type)}
              >
                <p>{`${state.type} focus`}</p>
                <MicroscopeSlider
                  leftLabel={state.left ? state.left : ""}
                  rightLabel={state.right ? state.right : ""}
                  onSliderChange={(value: number) => {
                    this.setFocusValue(state, value);

                    this.onChangeOfSliderPosition();
                    if (state.type === "Fine") {
                      this.setState({
                        fineBlur: state.value / 10,
                      });
                      this.setState({
                        fineSliderPosition: state.value / 10,
                      });
                    } else {
                      this.setState({
                        stage: state.value,
                      });
                      this.setState({
                        coarseSliderPosition: state.value / 10,
                      });
                      this.setState({
                        blur: state.value,
                      });
                      this.stagePosition = state.value;
                    }
                  }}
                  value={state.value}
                  step={state.step}
                  min={0}
                  max={100}
                  ariaLabel={`${state.type} focus slider`}
                />
              </div>
            ))}
          </div>
          <div className="sorterSettings__configs--lamp">
            <div
              className="sorterSettings__configs--lamp-wrapper"
              onClick={this.toggleLight}
              onKeyDown={this.toggleLight}
              role="button"
              tabIndex={0}
            >
              <img
                src={`images/sorter/light_${SimManager.getInstance().getState("MicroscopeLampLight")}.svg`}
                alt={`Lamp ${SimManager.getInstance().getState("MicroscopeLampLight")}`}
              />
            </div>
            <div className="sorterSettings__configs--lamp-slider">
              <MicroscopeSlider
                leftLabel={this.lightIntensitySlider.left}
                rightLabel={this.lightIntensitySlider.right}
                step={this.lightIntensitySlider.step}
                value={this.props.lightIntensity}
                onSliderChange={(value: number) => {
                  this.adjustLightIntensitySlider(value);
                }}
                min={0.25}
                max={1}
                ariaLabel="Lamp slider from dimmer to brighter"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  MicroscopeView = () => {
    return (
      <div className="microscopeView">
        <div
          ref={this.ref}
          aria-label="Microscope image view"
          className="microscopeView__imageView"
          style={{
            filter: `
                brightness(${
                  SimManager.getInstance().getState("MicroscopeLampLight") ===
                  "on"
                    ? this.props.lightIntensity
                    : 0.25
                })
              `,
            background: this.state.captured
              ? "#FFFFFF"
              : `url(${this.viewportStates()}) no-repeat`,
            backgroundSize: "cover",
          }}
        >
          {!this.state.captured && (
            <img
              className="microscopeView__imageView__slideImage"
              src={this.viewportStates()}
              alt="Specimen"
              style={{
                transform: `
                translate(${-this.props.navImagePosition.x * (this.props.selectedMagnification.value ? parseFloat(this.props.selectedMagnification.value) : 1)}%, 
                          ${-this.props.navImagePosition.y * (this.props.selectedMagnification.value ? parseFloat(this.props.selectedMagnification.value) : 1)}%)
                scale(${this.props.selectedMagnification.value ? parseFloat(this.props.selectedMagnification.value) : 1})
              `,
                transformOrigin: "center center",
                filter: `
                blur(${this.onChangeOfSliderPosition()}px)
              `,
              }}
            ></img>
          )}
          <div
            className="microscopeView__imageView__eclipse"
            data-html2canvas-ignore="true"
          ></div>
        </div>
        <div
          aria-label="Microscope body"
          className="microscopeView__microscopeBody"
        >
          <img
            className="baseMicroscope"
            src={"images/sorter/microscope_hook.svg"}
            alt="Microscope Body"
          />
          <img
            className="stageHeight"
            id="stage"
            src="images/sorter/stage heights/stage_2.svg"
            style={{
              transform: `translateY(${this.getStageHeight()}px)`,
            }}
            alt="Stage Height"
          />
          <div
            role="button"
            tabIndex={0}
            className="lightSwitch"
            onClick={this.toggleLight}
            onKeyDown={this.toggleLight}
          >
            <img
              src={`images/sorter/switch_${SimManager.getInstance().getState("MicroscopeLampLight")}.svg`}
              alt="Light Switch"
            />
          </div>
          <img className="nobs" src={this.props.knob} alt="Nobs" />
          <img
            className="nosepieceMagnification"
            src={this.renderNosepieceMagnification()}
            alt="Nosepiece Magnification"
          />
          {SimManager.getInstance().getState("MicroscopeLampLight") ===
            "on" && (
            <img
              className="lampLight"
              style={{
                opacity: `${SimManager.getInstance().getState("MicroscopeLampLight") === "on" ? (this.props.lightIntensity - 0.25) / 0.75 : 0}`,
              }}
              src={"images/sorter/lamp_light.svg"}
              alt="Lamp Light"
            />
          )}
        </div>
      </div>
    );
  };

  SorterView = () => {
    return (
      <div className="sorterView">
        {this.LenseMagnifications()}
        {this.Navigation()}
        {this.Settings()}
      </div>
    );
  };

  render() {
    return (
      <Modal open={this.props.isOpen} onClose={this.props.onClose}>
        <div
          className="sorter"
          style={{ width: window.innerWidth - 384 }}
          aria-label="This is your microscope. It has 3 lenses on a nosepiece at the end of a long arm that curves over the stage. It has some knobs, a light, and an on-off switch."
        >
          <div className="backbutton">
            <CloseButton backLabel="Back" close={this.props.onClose} />
          </div>
          {this.MicroscopeView()}
          {this.SorterView()}
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = (state: ModalState) => {
  const sorterState: ModalState = state.sorter;

  return {
    selectedMagnification: sorterState.selectedMagnification,
    magnifications: sorterState.magnifications,
    focusStates: sorterState.focusStates,
    lightIntensity: sorterState.lightIntensity,
    lightState: sorterState.lightState,
    navImagePosition: sorterState.navImagePosition,
    knob: sorterState.knob,
    viewPortImage: sorterState.viewPortImage,
    baseMicroscope: sorterState.baseMicroscope,
    viewPortImagePosition: sorterState.viewPortImagePosition,
    centeredViewPortPosition: sorterState.centeredViewPortPosition,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  setCurrentMagnification: (data: Magnification) =>
    dispatch(setCurrentMagnification(data)),
  changeFocus: (data: object) => dispatch(changeFocus(data)),
  adjustLight: (value: number) => dispatch(adjustLight(value)),
  toggleLight: () => dispatch(toggleLight()),
  changePosition: (data: string) => dispatch(changePosition(data)),
  changeViewportPosition: (data: string) =>
    dispatch(changeViewportPosition(data)),
  changeKnob: (data: string) => dispatch(changeKnob(data)),
  storeResultsImage: (data: ImageCapturing) =>
    dispatch(storeResultsImage(data)),
  updateMicroscopeImage: (data: string) =>
    dispatch(updateMicroscopeImage(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Sorter);
